import { Badge } from '@/components/ui/badge';
import { usePermission } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useBoards, useBoardMembers, useBoardPermission, useBoardSettingsTabs } from '@/hooks/use-boards';
import { useActiveSprint, useSprints } from '@/hooks/use-sprints';
import { useStatuses, useTasks, useProjectTags } from '@/hooks/use-tasks';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { BoardListItem } from '@hygge/shared';
import { CheckSquare, Columns2, Columns3, Filter, Keyboard, LayoutDashboard, LayoutList, List, ListTodo, Plus, Rows3, Settings } from 'lucide-react';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { BacklogColumnsView } from './backlog-columns-view';
import { BacklogView } from './backlog-view';
import { BoardSettingsDialog } from './board-settings-dialog';
import { BoardView } from './board-view';
import { BulkActionBar } from './bulk-action-bar';
import { CreateBoardDialog } from './create-board-dialog';
import { CreateTaskModal } from './create-task-modal';
import { ListView } from './list-view';
import { SprintInfoBar } from './sprint-info-bar';
import { TaskDetailPanel } from './task-detail-panel';
import { closeTaskSearch } from '../lib/task-nav';
import { truncationNotice } from '../lib/truncation-notice';
import { TasksDashboard, trackBoardVisit } from './tasks-dashboard';
import { AllIssuesView } from './all-issues-view';
import { BoardOverview } from './overview/board-overview';
import { BoardActivityTab } from './overview/board-activity-tab';
import {
  TaskFiltersBar,
  emptyFilters,
  activeFilterCount,
  hasActiveClientFilters,
  taskMatchesFilters,
  toQueryFilters,
  type FilterState,
} from './task-filters';
import { QuickFilters } from './quick-filters';

type ViewMode = 'overview' | 'activity' | 'board' | 'list' | 'backlog' | 'dashboard' | 'all-issues';
type BacklogLayout = 'rows' | 'columns';

const BACKLOG_LAYOUT_KEY = 'tasks-backlog-layout';

function getStoredBacklogLayout(): BacklogLayout {
  if (typeof window === 'undefined') return 'rows';
  return (localStorage.getItem(BACKLOG_LAYOUT_KEY) as BacklogLayout) || 'rows';
}

interface TasksPageProps {
  boardId?: string;
  taskKey?: string;
  view?: ViewMode;
}

export function TasksPage({ boardId: boardIdFromUrl, taskKey, view }: TasksPageProps) {
  const canCreateBoard = usePermission('boards.create');
  const navigate = useNavigate();
  // No permission to ask for: the board loaded, so the model admitted this
  // caller, and holding any role on a board is what reading it means.
  const canViewOverview = true;
  // Show dashboard when no board selected and no explicit view, or when view=dashboard
  const isDashboard = view === 'dashboard' || (!boardIdFromUrl && !view);
  // Overview is the default board view for users who can see it; others land on the board.
  const resolvedView: ViewMode = isDashboard
    ? 'dashboard'
    : (view ?? (canViewOverview ? 'overview' : 'board'));
  // Gracefully degrade a hand-crafted ?view=overview|activity|all-issues for users
  // without boards.view: overview/activity fall to the board; all-issues (which has
  // no board context) falls to the dashboard.
  const viewMode: ViewMode =
    !canViewOverview && (resolvedView === 'overview' || resolvedView === 'activity')
      ? 'board'
      : !canViewOverview && resolvedView === 'all-issues'
        ? 'dashboard'
        : resolvedView;
  const isAllIssues = viewMode === 'all-issues';
  const isIssuesView = viewMode === 'board' || viewMode === 'list' || viewMode === 'backlog';
  // Client-side filters apply to Board/List only — Backlog fetches its own sprint-grouped data.
  const isFilterableView = viewMode === 'board' || viewMode === 'list';
  const [backlogLayout, setBacklogLayout] = useState<BacklogLayout>(getStoredBacklogLayout);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);
  const [boardMenuOpen, setBoardMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [showFiltersBar, setShowFiltersBar] = useState(false);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [sprintInitialized, setSprintInitialized] = useState(false);

  // ─── Bulk select mode (Issues board/list only) ──────────────────────────
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const toggleSelect = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev) setSelectedKeys(new Set()); // exiting clears any ticked cards
      return !prev;
    });
  }, []);

  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const { data: boardsData, isLoading: isLoadingBoards, isFetching: isFetchingBoards, refetch: refetchBoards } = useBoards();
  // The board id a fresh fetch of the list has been asked to confirm. A board
  // missing from a CACHED list may simply be newer than the cache — a project's
  // board is created server-side — so the list is fetched once more before the
  // URL is treated as naming a board that does not exist.
  const [recheckedBoardId, setRecheckedBoardId] = useState<string | null>(null);
  const boards = boardsData ?? [];

  // Split boards into project boards and standalone boards
  const projectBoards = boards.filter((b: BoardListItem) => b.projectId);
  const standaloneBoards = boards.filter((b: BoardListItem) => !b.projectId);

  // Use board from URL, or fall back to first board
  // If URL board doesn't exist in the list (e.g. deleted), fall back to first available
  const urlBoardExists = boardIdFromUrl && boards.some((b: BoardListItem) => b.id === boardIdFromUrl);
  // A URL board missing from the list is being rechecked or redirected away
  // from; meanwhile no other board stands in for it, or its tasks and the `c`
  // shortcut would act on a board the link never named.
  const boardId = urlBoardExists ? boardIdFromUrl : boardIdFromUrl ? undefined : boards[0]?.id;
  // Asked once the board is known: what may be done here is this board’s to say.
  const canBulk = useBoardPermission(boardId, 'task.bulk');
  const canCreateTask = useBoardPermission(boardId, 'task.manage');
  // The gear opens a dialog whose tabs are each gated. With none of them, it
  // would open on nothing, so it is not offered.
  const settingsTabs = useBoardSettingsTabs(boardId);

  // Redirect if board from URL is missing/deleted
  useEffect(() => {
    if (isLoadingBoards || isFetchingBoards) return;

    if (boardIdFromUrl && !urlBoardExists && recheckedBoardId !== boardIdFromUrl) {
      setRecheckedBoardId(boardIdFromUrl);
      void refetchBoards();
      return;
    }

    if (boardIdFromUrl && !urlBoardExists && boards.length > 0) {
      // Board from URL was deleted or doesn't exist — redirect to dashboard
      navigate({ to: '/boards', search: {}, replace: true });
      return;
    }

    if (boardIdFromUrl && !urlBoardExists && boards.length === 0) {
      // No boards at all — clear URL
      navigate({ to: '/boards', search: {}, replace: true });
      return;
    }

    // No longer auto-redirect to first board — dashboard will show instead
  }, [boardIdFromUrl, urlBoardExists, boards, isLoadingBoards, isFetchingBoards, recheckedBoardId, refetchBoards, navigate]);

  // Track board visit for "recently visited" on dashboard
  useEffect(() => {
    if (boardId && urlBoardExists) trackBoardVisit(boardId);
  }, [boardId, urlBoardExists]);

  const { data: statusesData, isLoading: isLoadingStatuses } = useStatuses(boardId);
  const { data: activeSprint } = useActiveSprint(boardId);
  const { data: allSprints } = useSprints(boardId);
  const { data: boardMembers } = useBoardMembers(boardId);
  const { data: boardTags } = useProjectTags(boardId);

  // Default to active sprint when first loaded
  useEffect(() => {
    if (!sprintInitialized && activeSprint) {
      setSelectedSprintId(activeSprint.id);
      setSprintInitialized(true);
    }
  }, [activeSprint, sprintInitialized]);

  // Reset sprint selection when board changes
  useEffect(() => {
    setSprintInitialized(false);
    setSelectedSprintId(null);
  }, [boardId]);

  // Exit bulk select mode when the board or view changes.
  useEffect(() => {
    setSelectionMode(false);
    setSelectedKeys(new Set());
  }, [boardId, viewMode]);

  const queryFilters = useMemo(() => {
    const base = toQueryFilters({ ...filters, search: debouncedSearch });
    if (selectedSprintId && viewMode !== 'backlog' && !base.sprintId) {
      base.sprintId = selectedSprintId;
    }
    return base;
  }, [filters, debouncedSearch, selectedSprintId, viewMode]);
  const { data: tasksData, isLoading: isLoadingTasks } = useTasks(boardId, queryFilters);

  const statuses = statusesData ?? [];
  const tasks = tasksData?.data ?? [];

  // How many issues the board actually holds, per the server. `tasks` is one
  // page of at most TASKS_PAGE_LIMIT, so on a big board it is a slice and its
  // length is not a total — reporting it as one is how this screen used to
  // claim "100 of 100" on a board of 2048.
  const boardTotal = tasksData?.meta.total ?? tasks.length;
  const isTruncated = boardTotal > tasks.length;
  // The notice names the escape hatches — but only the ones on this screen: the
  // sprint picker below renders on a board that has sprints, and the board where
  // this notice fires hardest (2,048 issues) has none.
  const truncationLine = truncationNotice({
    loaded: tasks.length,
    total: boardTotal,
    hasSprintPicker: (allSprints?.length ?? 0) > 0,
  });

  // Client-side dimension filters (status/priority/type/assignee/label/overdue)
  // are applied over the loaded set — no server round-trip on change.
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const visibleTasks = useMemo(
    () => tasks.filter((t) => taskMatchesFilters(t, filters, todayIso)),
    [tasks, filters, todayIso],
  );

  // Keep the selection confined to what's on screen: when a filter hides a
  // selected task, drop it, so a bulk action never touches a task you can't see.
  useEffect(() => {
    if (!selectionMode) return;
    setSelectedKeys((prev) => {
      if (prev.size === 0) return prev;
      const visibleKeys = new Set(visibleTasks.map((t) => t.key));
      let changed = false;
      const next = new Set<string>();
      for (const key of prev) {
        if (visibleKeys.has(key)) next.add(key);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleTasks, selectionMode]);

  const selectedBoard = boards.find((b: BoardListItem) => b.id === boardId);

  const handleViewChange = useCallback((mode: ViewMode) => {
    if (mode === 'dashboard') {
      navigate({ to: '/boards', search: {} });
      return;
    }
    navigate({
      to: '/boards',
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        // Overview is the default board view, so it maps to a clean URL (no `view`).
        view: mode === 'overview' ? undefined : mode,
      }),
    });
  }, [navigate]);

  const handleBacklogLayoutChange = (layout: BacklogLayout) => {
    setBacklogLayout(layout);
    localStorage.setItem(BACKLOG_LAYOUT_KEY, layout);
  };

  const handleBoardChange = useCallback((id: string) => {
    setFilters(emptyFilters());
    navigate({
      to: '/boards',
      search: (prev: Record<string, unknown>) => ({ ...prev, board: id, task: undefined }),
    });
  }, [navigate]);

  const handleTaskSelect = useCallback((key: string) => {
    navigate({ to: '/boards', search: (prev: Record<string, unknown>) => ({ ...prev, task: key }) });
  }, [navigate]);

  const handleTaskClose = useCallback(() => {
    navigate({ to: '/boards', search: (prev: Record<string, unknown>) => closeTaskSearch(prev) });
  }, [navigate]);

  const handleBoardCreated = useCallback((newBoardId: string) => {
    navigate({
      to: '/boards',
      search: (prev: Record<string, unknown>) => ({ ...prev, board: newBoardId, task: undefined }),
    });
  }, [navigate]);

  const isLoading = isLoadingBoards || isLoadingStatuses || isLoadingTasks;
  const activeCount = activeFilterCount(filters);
  const filtersActive =
    hasActiveClientFilters(filters) || filters.search !== '' || filters.sprintId !== 'all';
  const showFilters = viewMode === 'list' || showFiltersBar || filtersActive;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      // Creating is `task.manage`; a role without it is not offered the shortcut.
      if (e.key === 'c' && !e.metaKey && !e.ctrlKey && boardId && canCreateTask && !isCreating && !taskKey) {
        e.preventDefault();
        setIsCreating(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [boardId, canCreateTask, isCreating, taskKey]);

  const handleDashboardBoardSelect = useCallback((id: string) => {
    navigate({
      to: '/boards',
      search: { board: id },
    });
  }, [navigate]);

  // ─── All issues (cross-board) view ──────────────────────────────────
  if (isAllIssues) {
    return (
      <>
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <button
              onClick={() => navigate({ to: '/boards', search: {} })}
              className="font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              Boards
            </button>
            <span>/</span>
            <span className="font-medium text-foreground">All issues</span>
          </div>
        </header>
        <div className="flex flex-col h-[calc(100vh-7rem)]">
          <AllIssuesView onTaskSelect={handleTaskSelect} />
        </div>

        {taskKey && (
          <TaskDetailPanel taskKey={taskKey} onClose={handleTaskClose} />
        )}
      </>
    );
  }

  // ─── Dashboard view ─────────────────────────────────────────────────
  if (viewMode === 'dashboard') {
    return (
      <>
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span className="font-medium text-foreground">Boards</span>
          </div>
        </header>
        <div className="flex flex-col h-[calc(100vh-7rem)]">
          <TasksDashboard onBoardSelect={handleDashboardBoardSelect} />
        </div>
      </>
    );
  }

  // Top-level tabs: Overview / Activity gated behind boards.view; Issues always shown.
  const boardTabs: { key: ViewMode; label: string; active: boolean }[] = [
    ...(canViewOverview
      ? ([
          { key: 'overview', label: 'Overview', active: viewMode === 'overview' },
          { key: 'activity', label: 'Activity', active: viewMode === 'activity' },
        ] as { key: ViewMode; label: string; active: boolean }[])
      : []),
    { key: 'board', label: 'Issues', active: isIssuesView },
  ];

  return (
    <>
      {/* Breadcrumb */}
      <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60 px-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Home</span>
          <span>/</span>
          <button
            onClick={() => navigate({ to: '/boards', search: {} })}
            className="font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          >
            Boards
          </button>
          {selectedBoard && (
            <>
              <span>/</span>
              <span className="font-medium text-foreground">{selectedBoard.name}</span>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-col h-[calc(100vh-7rem)]">
        {/* Top-level tabs */}
        {selectedBoard && (
          <nav className="flex items-center gap-5 border-b border-border bg-background px-6">
            {boardTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleViewChange(tab.key)}
                className={cn(
                  'relative py-3 text-sm font-medium transition-colors cursor-pointer',
                  tab.active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {tab.label}
                {tab.active && (
                  <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-hygge" />
                )}
              </button>
            ))}
          </nav>
        )}

        {/* Toolbar (Issues views only) */}
        {isIssuesView && (
        <TooltipProvider delayDuration={0}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-background">
          <div className="flex items-center gap-3">
            {isLoadingBoards ? (
              <Skeleton className="h-9 w-48" />
            ) : (
              <div className="flex items-center gap-2">
                <Select
                  open={boardMenuOpen}
                  onOpenChange={setBoardMenuOpen}
                  value={boardId ?? ''}
                  onValueChange={handleBoardChange}
                >
                  <SelectTrigger className="w-56 h-9 text-sm">
                    <SelectValue placeholder="Select board" />
                  </SelectTrigger>
                  <SelectContent
                    footer={
                      canCreateBoard ? (
                      <button
                        type="button"
                        onClick={() => {
                          setBoardMenuOpen(false);
                          setIsCreatingBoard(true);
                        }}
                        className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-sm font-medium text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground cursor-pointer"
                      >
                        <Plus className="h-4 w-4" />
                        New board
                      </button>
                      ) : null
                    }
                  >
                    {projectBoards.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="text-xs font-semibold text-muted-foreground">Project Boards</SelectLabel>
                        {projectBoards.map((b: BoardListItem) => (
                          <SelectItem key={b.id} value={b.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="h-2.5 w-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: b.color }}
                              />
                              <span className="truncate">{b.name}</span>
                              <span className="text-xs text-muted-foreground font-mono ml-1">{b.slug}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                    {standaloneBoards.length > 0 && (
                      <SelectGroup>
                        <SelectLabel className="text-xs font-semibold text-muted-foreground">Standalone Boards</SelectLabel>
                        {standaloneBoards.map((b: BoardListItem) => (
                          <SelectItem key={b.id} value={b.id}>
                            <div className="flex items-center gap-2">
                              <div
                                className="h-2.5 w-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: b.color }}
                              />
                              <span className="truncate">{b.name}</span>
                              <span className="text-xs text-muted-foreground font-mono ml-1">{b.slug}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedBoard && (
              <Badge variant="outline" className="text-xs px-2 py-0.5 gap-1.5 font-normal">
                {boardTotal} task{boardTotal !== 1 ? 's' : ''}
              </Badge>
            )}

            {selectedBoard && isFilterableView && (
              <QuickFilters filters={filters} onChange={setFilters} />
            )}
          </div>

            <div className="flex items-center gap-2">
              {/* View switcher.
                  Named, not just drawn. Four unlabelled icons meant the only way
                  to find out what any of them did was to hover each in turn —
                  and the difference between the four ways of looking at a board
                  is the least guessable thing on this toolbar. The active one
                  was marked by a background alone, which at this size reads as
                  nothing.
                  The labels collapse below `lg`, where the tooltip and the
                  aria-label still carry the name; on a screen reader they were
                  four buttons called nothing at all, since a tooltip is not an
                  accessible name. */}
              <div
                role="group"
                aria-label="View"
                className="flex items-center rounded-full border border-border bg-muted/30 p-0.5"
              >
                {([
                  { mode: 'dashboard' as const, icon: LayoutDashboard, label: 'Dashboard' },
                  { mode: 'backlog' as const, icon: LayoutList, label: 'Backlog' },
                  { mode: 'board' as const, icon: Columns3, label: 'Board' },
                  { mode: 'list' as const, icon: List, label: 'List' },
                ] as const).map(({ mode, icon: Icon, label }) => (
                  <Tooltip key={mode}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={label}
                        aria-pressed={viewMode === mode}
                        className={cn(
                          'h-7 gap-1.5 rounded-full px-2 text-xs font-medium cursor-pointer lg:px-2.5',
                          viewMode === mode
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                        onClick={() => handleViewChange(mode)}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="hidden lg:inline">{label}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom"><p>{label}</p></TooltipContent>
                  </Tooltip>
                ))}
              </div>

              <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />

              {/* Select mode toggle (board/list only, gated on bulk permission) */}
              {selectedBoard && canBulk && isFilterableView && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn(
                        'h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground cursor-pointer',
                        selectionMode &&
                          'border-hygge/30 bg-hygge/10 text-hygge hover:bg-hygge/20 hover:text-hygge',
                      )}
                      onClick={toggleSelectionMode}
                      aria-label="Select tasks"
                      aria-pressed={selectionMode}
                    >
                      <CheckSquare className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p>{selectionMode ? 'Exit select mode' : 'Select tasks'}</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Actions — round outline icon buttons */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0 rounded-full border-hygge/30 text-hygge hover:bg-hygge/10 hover:text-hygge cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setIsCreating(true)}
                    disabled={!boardId || !canCreateTask}
                    aria-label="New task"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom"><p>New task</p></TooltipContent>
              </Tooltip>

              {settingsTabs.first && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => setIsSettingsOpen(true)}
                      disabled={!boardId}
                      aria-label="Board settings"
                    >
                      <Settings className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p>Board settings</p></TooltipContent>
                </Tooltip>
              )}

              {viewMode === 'board' && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      className={cn(
                        'relative h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground cursor-pointer',
                        (showFiltersBar || filtersActive) && 'border-hygge/30 bg-hygge/10 text-hygge hover:bg-hygge/20 hover:text-hygge',
                      )}
                      onClick={() => setShowFiltersBar((p) => !p)}
                      aria-label="Filter"
                    >
                      <Filter className="h-4 w-4" />
                      {activeCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-cta px-1 text-[9px] font-medium text-cta-foreground">
                          {activeCount}
                        </span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom"><p>Filter</p></TooltipContent>
                </Tooltip>
              )}
            </div>
        </div>
        </TooltipProvider>
        )}

        {/* Truncation notice. Shown outside the filter bar because the board
            view hides that bar by default, and a board silently drawing 100 of
            2048 cards is exactly where the omission does the most damage. */}
        {isFilterableView && truncationLine && (
          <div className="border-b border-border bg-muted/10 px-6 py-1.5 text-xs text-muted-foreground">
            {truncationLine}
          </div>
        )}

        {/* Filter bar — always visible in list view, toggleable in board view */}
        {isFilterableView && showFilters && (
          <TaskFiltersBar
            filters={filters}
            onChange={setFilters}
            statuses={statuses}
            sprints={allSprints}
            members={boardMembers ?? []}
            labelOptions={(boardTags ?? []).map((t) => t.name)}
            shownCount={visibleTasks.length}
            totalCount={tasks.length}
            truncated={isTruncated}
          />
        )}

        {/* Sprint selector bar (board/list views) */}
        {isIssuesView && allSprints && allSprints.length > 0 && viewMode !== 'backlog' && (
          <SprintInfoBar
            sprints={allSprints}
            selectedSprintId={selectedSprintId}
            onSprintChange={setSelectedSprintId}
          />
        )}

        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {/* A URL board missing from the cached list is being rechecked: that is
              loading, not "no boards" — the empty state offered Create Board to
              somebody who had boards. */}
          {!boardId && !isLoadingBoards && !(boardIdFromUrl && !urlBoardExists) ? (
            <EmptyState onCreateBoard={() => setIsCreatingBoard(true)} />
          ) : !boardId ? (
            // Boards list still resolving — never mount a view with an undefined board id.
            <LoadingSkeleton viewMode={viewMode} />
          ) : isLoading && (viewMode === 'board' || viewMode === 'list') ? (
            <LoadingSkeleton viewMode={viewMode} />
          ) : viewMode === 'overview' ? (
            <BoardOverview
              boardId={boardId}
              onSeeAllActivity={() => handleViewChange('activity')}
              onOpenTask={(key) => handleTaskSelect(key)}
            />
          ) : viewMode === 'activity' ? (
            <BoardActivityTab boardId={boardId} />
          ) : viewMode === 'backlog' ? (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Backlog header with layout toggle */}
              <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/50 shrink-0">
                {/* The tab above already says "Backlog". Repeating it here spent the
                    one line that could have said what the screen is for — which is
                    the thing nobody could tell by looking at it. */}
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-foreground">Sprint planning</h2>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {/* Moving issues is `task.manage`; without it the drag and the
                        arrow are not there, so the line must not offer them. */}
                    {canCreateTask
                      ? 'Drag issues between the backlog and the sprint, or hover a row and use the arrow.'
                      : 'Your role on this board can view the plan but not move issues.'}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <TooltipProvider delayDuration={200}>
                    {/* The keyboard shortcuts existed and nothing on screen mentioned
                        them, which is the same as not having them. */}
                    {backlogLayout === 'rows' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 cursor-pointer text-muted-foreground"
                            aria-label="Keyboard shortcuts"
                          >
                            <Keyboard className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="end" className="max-w-none">
                          <ul className="space-y-1 text-xs">
                            {[
                              ['↑ ↓ / j k', 'move between issues'],
                              ['m', 'move the focused issue in or out of the sprint'],
                              ['Space', 'select · Enter opens'],
                              ['Esc', 'clear selection'],
                            ].map(([keys, what]) => (
                              <li key={keys} className="flex items-baseline gap-2">
                                <kbd className="rounded border border-border/60 px-1 font-mono text-[10px]">
                                  {keys}
                                </kbd>
                                <span className="text-muted-foreground">{what}</span>
                              </li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <div className="flex items-center rounded-md border border-border bg-muted/30 p-0.5">
                      {([
                        { layout: 'rows' as const, icon: Rows3, label: 'List' },
                        { layout: 'columns' as const, icon: Columns2, label: 'Columns' },
                      ] as const).map(({ layout, icon: Icon, label }) => (
                        <Tooltip key={layout}>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                'h-6 w-6 cursor-pointer',
                                backlogLayout === layout && 'bg-background shadow-sm',
                              )}
                              onClick={() => handleBacklogLayoutChange(layout)}
                            >
                              <Icon className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom"><p>{label}</p></TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </TooltipProvider>
                </div>
              </div>

              {/* Backlog content */}
              <div className="flex-1 overflow-hidden">
                {backlogLayout === 'columns' ? (
                  <BacklogColumnsView
                    projectId={boardId!}
                    statuses={statuses}
                    sprintId={selectedSprintId}
                    onTaskSelect={handleTaskSelect}
                    // Only offered where the board lets this role create tasks.
                    onCreateTask={canCreateTask ? () => setIsCreating(true) : undefined}
                  />
                ) : (
                  <BacklogView
                    projectId={boardId!}
                    statuses={statuses}
                    onTaskSelect={handleTaskSelect}
                    paused={!!taskKey || isCreating}
                  />
                )}
              </div>
            </div>
          ) : tasks.length === 0 && !filtersActive && statuses.length === 0 ? (
            <EmptyBoardState onCreateTask={canCreateTask ? () => setIsCreating(true) : undefined} />
          ) : viewMode === 'board' ? (
            <BoardView
              projectId={boardId!}
              statuses={statuses}
              tasks={visibleTasks}
              onTaskSelect={handleTaskSelect}
              selectionMode={selectionMode}
              selectedKeys={selectedKeys}
              onToggleSelect={toggleSelect}
            />
          ) : (
            <div className="p-6 h-full overflow-auto">
              <ListView
                tasks={visibleTasks}
                statuses={statuses}
                onTaskSelect={handleTaskSelect}
                selectionMode={selectionMode}
                selectedKeys={selectedKeys}
                onToggleSelect={toggleSelect}
              />
            </div>
          )}
        </div>
      </div>

      {/* Bulk action bar (floating, board/list select mode) */}
      {canBulk && selectionMode && isFilterableView && boardId && (
        <BulkActionBar
          boardId={boardId}
          statuses={statuses}
          members={boardMembers ?? []}
          selectedKeys={selectedKeys}
          onDone={clearSelection}
          // A finished bulk action ends select mode (HW-820); Clear only unticks.
          onSucceeded={() => setSelectionMode(false)}
        />
      )}

      {/* Create task modal */}
      {isCreating && boardId && (
        <CreateTaskModal
          projectId={boardId}
          statuses={statuses}
          defaultSprintId={selectedSprintId && viewMode !== 'backlog' ? selectedSprintId : undefined}
          sprintName={selectedSprintId && viewMode !== 'backlog' ? allSprints?.find((s) => s.id === selectedSprintId)?.name : undefined}
          onClose={() => setIsCreating(false)}
        />
      )}

      {/* Create board dialog */}
      <CreateBoardDialog
        open={isCreatingBoard}
        onOpenChange={setIsCreatingBoard}
        onCreated={handleBoardCreated}
      />

      {/* Task detail panel */}
      {taskKey && (
        <TaskDetailPanel
          taskKey={taskKey}
          projectId={boardId}
          onClose={handleTaskClose}
        />
      )}

      {/* Board settings dialog */}
      {boardId && (
        <BoardSettingsDialog
          projectId={boardId}
          open={isSettingsOpen}
          onOpenChange={setIsSettingsOpen}
          onBoardDeleted={() => {
            // Hard navigate to /tasks to ensure clean state after board deletion
            window.location.href = '/boards';
          }}
        />
      )}

    </>
  );
}

/** `onCreateTask` absent: the role may not create tasks here, so nothing invites it. */
function EmptyBoardState({ onCreateTask }: { onCreateTask?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
        <ListTodo className="h-7 w-7 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-semibold text-foreground mb-1">No tasks yet</h3>
      {onCreateTask ? (
        <>
          <p className="text-xs text-muted-foreground max-w-xs mb-5">
            Get started by creating your first task. Use the board to organize work across statuses.
          </p>
          <Button
            size="sm"
            className="gap-1.5 cursor-pointer"
            onClick={onCreateTask}
          >
            <Plus className="h-4 w-4" />
            Create First Task
          </Button>
          <p className="text-[10px] text-muted-foreground mt-3 font-mono">
            Tip: press <kbd className="px-1 py-0.5 rounded bg-muted text-[10px]">C</kbd> to create a task anytime
          </p>
        </>
      ) : (
        <p className="text-xs text-muted-foreground max-w-xs">This board has no tasks yet.</p>
      )}
    </div>
  );
}

function EmptyState({ onCreateBoard }: { onCreateBoard: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mb-4">
        <Columns3 className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-sm font-medium text-foreground mb-1">No boards found</h3>
      <p className="text-xs text-muted-foreground max-w-sm mb-5">
        Create a board to start managing tasks. Choose a template to get started quickly.
      </p>
      <Button
        size="sm"
        className="gap-1.5 cursor-pointer"
        onClick={onCreateBoard}
      >
        <Plus className="h-4 w-4" />
        Create Board
      </Button>
    </div>
  );
}

function LoadingSkeleton({ viewMode }: { viewMode: ViewMode }) {
  if (viewMode === 'list') {
    return (
      <div className="p-6 space-y-2">
        <Skeleton className="h-8 w-full rounded-md" />
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full rounded-md" />
        ))}
      </div>
    );
  }
  return (
    <div className="flex gap-4 p-6 h-full">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="shrink-0 w-72">
          <Skeleton className="h-8 w-full mb-3 rounded-md" />
          <div className="space-y-2">
            {Array.from({ length: 3 - i }).map((_, j) => (
              <Skeleton key={j} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
